// src/components/MyTraining.js

import React, { useState, useEffect } from 'react';
import api from '../api';
import { Link } from 'react-router-dom';
import { IoCheckmarkCircle, IoSaveOutline, IoCloseOutline } from 'react-icons/io5';
import { toast } from 'react-hot-toast';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';

const MyTraining = () => {
    const [assignment, setAssignment] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [logData, setLogData] = useState({});
    const [recentlyLogged, setRecentlyLogged] = useState(new Set());
    const [exerciseModal, setExerciseModal] = useState({
        open: false,
        loading: false,
        data: null,
        tab: 'description',
    });

    useEffect(() => {
        const fetchInitialData = async () => {
            try {
                const programResponse = await api.get('/me/training-program');
                const programData = programResponse.data;
                if (!programData) {
                    setLoading(false);
                    return;
                }
                setAssignment(programData);

                const logsResponse = await api.get(`/me/logs/today?program_id=${programData.program.id}`);
                const todaysLogs = logsResponse.data;

                const initialLogData = {};
                todaysLogs.forEach(log => {
                    initialLogData[log.routine_exercise_id] = {
                        // --- CAMBIO: Leer de 'weight_lifted_lbs' ---
                        weight: log.weight_lifted_lbs !== null ? log.weight_lifted_lbs : '',
                        reps: log.reps_completed !== null ? log.reps_completed : ''
                    };
                });
                setLogData(initialLogData);

            } catch (err) {
                console.error("Error al cargar datos de entrenamiento:", err);
                setError("Error al cargar tus datos de entrenamiento.");
            } finally {
                setLoading(false);
            }
        };
        fetchInitialData();
    }, []);

    const handleInputChange = (routineExerciseId, field, value) => {
        setLogData(prev => ({
            ...prev,
            [routineExerciseId]: {
                ...prev[routineExerciseId],
                [field]: value
            }
        }));
    };

    const handleLogSubmit = async (routineExerciseId) => {
        const data = logData[routineExerciseId] || {};
        const payload = {
            routine_exercise_id: routineExerciseId,
            // --- CAMBIO: Enviar como 'weight_lifted_lbs' ---
            weight_lifted_lbs: parseFloat(data.weight) || null,
            reps_completed: parseInt(data.reps, 10) || null,
        };

        try {
            await api.post('/me/logs', payload);
            setRecentlyLogged(prev => new Set(prev).add(routineExerciseId));
            setTimeout(() => {
                setRecentlyLogged(prev => {
                    const newSet = new Set(prev);
                    newSet.delete(routineExerciseId);
                    return newSet;
                });
            }, 2000);

        } catch (err) {
            toast.error('Error al guardar el registro. Intentalo de nuevo.');
            console.error(err);
        }
    };

    const getYoutubeEmbedUrl = (url) => {
        if (!url) return null;
        try {
            const parsed = new URL(url);
            if (parsed.hostname.includes('youtu.be')) {
                return `https://www.youtube.com/embed/${parsed.pathname.replace('/', '')}`;
            }
            if (parsed.hostname.includes('youtube.com')) {
                const v = parsed.searchParams.get('v');
                return v ? `https://www.youtube.com/embed/${v}` : null;
            }
            return null;
        } catch {
            return null;
        }
    };

    const openExerciseModal = async (exerciseId) => {
        setExerciseModal({ open: true, loading: true, data: null, tab: 'description' });
        try {
            const res = await api.get(`/admin/exercises/${exerciseId}`);
            setExerciseModal({ open: true, loading: false, data: res.data, tab: 'description' });
        } catch {
            setExerciseModal({ open: true, loading: false, data: null, tab: 'description' });
            toast.error('No se pudo cargar el detalle del ejercicio.');
        }
    };

    if (loading) return <p className="text-white text-center py-10">Cargando tu programa...</p>;
    if (error) return <p className="text-red-500 text-center py-10">{error}</p>;
    if (!assignment) return (
        <div className="w-full container mx-auto p-8 text-center text-white">
            <h2 className="text-3xl font-bold">Sin Programa Activo</h2>
            <p className="text-gray-400 mt-2">Aún no tienes un programa de entrenamiento asignado o activo. Por favor, contacta a tu coach.</p>
            <Link to="/dashboard" className="mt-6 inline-block bg-marizan-red text-white font-bold py-2 px-4 rounded-lg">
                Volver al Dashboard
            </Link>
        </div>
    );

    const { program } = assignment;

    const getGroupTypeName = (type) => {
        switch (type) {
            case 'superset': return 'Super-Set';
            case 'triset': return 'Tri-Set';
            case 'circuit': return 'Circuito';
            default: return 'Serie';
        }
    };

    return (
        <div className="w-full container mx-auto p-4 md:p-8 text-white">
            <h1 className="text-4xl md:text-5xl font-bold mb-2">{program.name}</h1>
            <p className="text-gray-400 mb-8">{program.description}</p>

            <div className="space-y-8">
                {program.routines.map((routine, index) => (
                    <div key={routine.id} className="bg-black bg-opacity-40 p-6 rounded-lg shadow-xl border border-gray-700">
                        <h2 className="text-2xl font-bold text-white border-b border-gray-600 pb-3 mb-4">
                            {routine.name || `Día ${index + 1}`}
                        </h2>

                        <div className="space-y-6">
                            {routine.exercise_groups.map((group, groupIndex) => (
                                <div key={group.id} className="bg-gray-900 p-4 rounded-lg">
                                    <div className="md:flex justify-between items-baseline mb-3 pb-3 border-b border-gray-700">
                                        <h3 className="text-xl font-bold text-marizan-red mb-2 md:mb-0">
                                            {getGroupTypeName(group.group_type)} - Bloque {groupIndex + 1}
                                        </h3>
                                        <div className="flex items-center space-x-4 text-gray-300">
                                            <span className="text-lg font-semibold">{group.sets} Sets</span>
                                            {(group.group_type !== 'simple') && (
                                                <span className="text-sm">Desc. entre Ej: <strong>{group.rest_between_exercises_seconds}s</strong></span>
                                            )}
                                            <span className="text-sm">Desc. entre Sets: <strong>{group.rest_between_sets_seconds}s</strong></span>
                                        </div>
                                    </div>
                                    
                                    {/* --- CAMBIO: Etiqueta de la cabecera --- */}
                                    <div className="hidden md:grid grid-cols-12 gap-4 text-sm font-bold text-gray-400 mb-2 px-2 items-center">
                                        <div className="col-span-4">EJERCICIO</div>
                                        <div className="col-span-2 text-center">OBJETIVO (REPS)</div>
                                        <div className="col-span-2 text-center">PESO REAL (lbs)</div> {/* <-- CORREGIDO */}
                                        <div className="col-span-2 text-center">REPS REALES</div>
                                        <div className="col-span-2 text-center">ACCIÓN</div>
                                    </div>

                                    <ul className="space-y-3">
                                        {group.exercises.map(routineEx => {
                                            const isRecentlyLogged = recentlyLogged.has(routineEx.id);
                                            const hasExistingData = logData[routineEx.id] && (logData[routineEx.id].weight || logData[routineEx.id].reps);

                                            return (
                                                <li key={routineEx.id} className={`p-3 rounded-md grid grid-cols-12 gap-x-4 gap-y-2 items-center transition-colors duration-300 ${isRecentlyLogged ? 'bg-green-600' : (hasExistingData ? 'bg-blue-900 bg-opacity-40' : 'bg-gray-800')}`}>
                                                    <div className="col-span-12 md:col-span-4">
                                                        <button
                                                            type="button"
                                                            onClick={() => openExerciseModal(routineEx.exercise.id)}
                                                            className="font-semibold hover:underline text-left"
                                                        >
                                                            {routineEx.exercise.name}
                                                        </button>
                                                        <div className="mt-1">
                                                            <Link to={`/exercises/${routineEx.exercise.id}`} className="text-xs text-gray-400 hover:text-white underline">
                                                                Abrir página completa
                                                            </Link>
                                                        </div>
                                                        {routineEx.technique_notes && (
                                                            <p className="text-yellow-400 text-xs mt-1">Técnica: {routineEx.technique_notes}</p>
                                                        )}
                                                    </div>
                                                    <div className="col-span-12 md:col-span-2 text-center text-gray-300 text-lg">
                                                        <span className="md:hidden font-bold text-sm mr-2">Objetivo:</span>{routineEx.reps}
                                                    </div>
                                                    
                                                    {/* --- CAMBIO: placeholder del input --- */}
                                                    <div className="col-span-6 md:col-span-2">
                                                        <input type="number" placeholder="lbs" className="bg-gray-700 w-full p-2 rounded text-center text-white" value={logData[routineEx.id]?.weight || ''} onChange={(e) => handleInputChange(routineEx.id, 'weight', e.target.value)} />
                                                    </div>
                                                    
                                                    <div className="col-span-6 md:col-span-2">
                                                        <input type="number" placeholder="reps" className="bg-gray-700 w-full p-2 rounded text-center text-white" value={logData[routineEx.id]?.reps || ''} onChange={(e) => handleInputChange(routineEx.id, 'reps', e.target.value)} />
                                                    </div>
                                                    <div className="col-span-12 md:col-span-2 flex justify-center">
                                                        {isRecentlyLogged ? (
                                                            <div className="flex items-center text-white font-bold bg-green-500 py-2 px-4 rounded-lg w-full justify-center">
                                                                <IoCheckmarkCircle className="mr-2" size={22} />Guardado
                                                            </div>
                                                        ) : (
                                                            <button onClick={() => handleLogSubmit(routineEx.id)} className="flex items-center justify-center bg-marizan-red hover:bg-red-700 text-white font-bold py-2 px-4 rounded-lg w-full">
                                                                <IoSaveOutline className="mr-2" />
                                                                {hasExistingData ? 'Actualizar' : 'Guardar'}
                                                            </button>
                                                        )}
                                                    </div>
                                                </li>
                                            );
                                        })}
                                    </ul>
                                </div>
                            ))}
                        </div>
                    </div>
                ))}
            </div>

            {exerciseModal.open && (
                <div className="fixed inset-0 bg-black/80 backdrop-blur-sm z-50 p-3 md:p-6 flex items-end md:items-center justify-center">
                    <div className="w-full max-w-2xl max-h-[92vh] overflow-hidden rounded-2xl border border-white/10 bg-[#0d121c] shadow-2xl flex flex-col">
                        <div className="px-4 py-3 border-b border-white/10 flex items-center justify-between">
                            <div className="min-w-0">
                                <h3 className="text-white font-bold truncate">{exerciseModal.data?.name || 'Detalle del ejercicio'}</h3>
                                <p className="text-xs text-gray-400 truncate">{exerciseModal.data?.category || '—'}</p>
                            </div>
                            <button
                                type="button"
                                className="text-gray-400 hover:text-white p-1.5 rounded-md hover:bg-white/10"
                                onClick={() => setExerciseModal({ open: false, loading: false, data: null, tab: 'description' })}
                            >
                                <IoCloseOutline size={20} />
                            </button>
                        </div>

                        <div className="px-4 pt-3 border-b border-white/10 flex gap-2 overflow-x-auto">
                            {['description', 'video', 'images'].map((tab) => (
                                <button
                                    key={tab}
                                    type="button"
                                    onClick={() => setExerciseModal((prev) => ({ ...prev, tab }))}
                                    className={`px-3 py-1.5 rounded-t-md text-sm whitespace-nowrap ${exerciseModal.tab === tab ? 'bg-marizan-red/20 text-marizan-red border-b-2 border-marizan-red' : 'text-gray-400 hover:text-gray-200'}`}
                                >
                                    {tab === 'description' ? 'Descripción' : tab === 'video' ? 'Video' : 'Imágenes'}
                                </button>
                            ))}
                        </div>

                        <div className="overflow-y-auto p-4">
                            {exerciseModal.loading && <p className="text-gray-300">Cargando detalle...</p>}

                            {!exerciseModal.loading && !exerciseModal.data && (
                                <p className="text-red-400">No se pudo cargar la información.</p>
                            )}

                            {!exerciseModal.loading && exerciseModal.data && exerciseModal.tab === 'description' && (
                                <div className="prose prose-invert max-w-none text-gray-200 text-sm md:text-base">
                                    <ReactMarkdown remarkPlugins={[remarkGfm]}>
                                        {exerciseModal.data.description || 'No hay descripción disponible.'}
                                    </ReactMarkdown>
                                </div>
                            )}

                            {!exerciseModal.loading && exerciseModal.data && exerciseModal.tab === 'video' && (
                                <div>
                                    {getYoutubeEmbedUrl(exerciseModal.data.video_url) ? (
                                        <div className="w-full aspect-video rounded-lg overflow-hidden border border-white/10">
                                            <iframe
                                                src={getYoutubeEmbedUrl(exerciseModal.data.video_url)}
                                                title="Video del ejercicio"
                                                className="w-full h-full"
                                                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                                                allowFullScreen
                                            />
                                        </div>
                                    ) : exerciseModal.data.video_url ? (
                                        <a
                                            href={exerciseModal.data.video_url}
                                            target="_blank"
                                            rel="noopener noreferrer"
                                            className="text-marizan-red underline"
                                        >
                                            Abrir video externo
                                        </a>
                                    ) : (
                                        <p className="text-gray-400">No hay video disponible.</p>
                                    )}
                                </div>
                            )}

                            {!exerciseModal.loading && exerciseModal.data && exerciseModal.tab === 'images' && (
                                <div>
                                    {exerciseModal.data.images?.length ? (
                                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                                            {exerciseModal.data.images.map((img) => (
                                                <a href={img.url} target="_blank" rel="noopener noreferrer" key={img.id} className="block rounded-lg overflow-hidden border border-white/10">
                                                    <img src={img.url} alt={exerciseModal.data.name} className="w-full h-44 object-cover" />
                                                </a>
                                            ))}
                                        </div>
                                    ) : (
                                        <p className="text-gray-400">No hay imágenes disponibles.</p>
                                    )}
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default MyTraining;